import isArray from 'lodash/isArray';
import isString from 'lodash/isString';
import isObject from 'lodash/isObject';
import isFunction from 'lodash/isFunction';

import { isEmpty } from 'lodash';
import Communication from './Communication';
import UserRole from './UserRole.enum';
import Util from './Util';

import { Carrier } from '../data/carriers';
import { Capability, InternalCapability, AccountStatus } from '../data/account';
import ChatbotInitializer from '../chatbot/ChatbotInitializer';

// a query param for navigation from ATG: storeContext
export const STORE_CONTEXT_TYPE = {
	WEBCLIENT: 'webclient', // previously 'customer'
	WINCLIENT: 'winclient', // previously 'clientstore'
	WEBSTORE: 'webstore', // previously 'webstore'
	ATGSTORE: 'atgwebstore',
	INTEGRATOR: 'integrator', // WEBAPPS-28746, for integrators to use the account pages from GetURL
};

const ChangeType = {
	All: 'all',
	AccountInfo: 'accountInfo',
	Balance: 'balance',
	Brand: 'brand',
	CalculatedSettings: 'calculatedSettings',
	ContainerLabelCount: 'containerLabelCount',
	RefundAssistLabelCount: 'refundAssistLabelCount',
	InternalAccountInfo: 'internalAccountInfo',
	Preferences: 'preferences',
	Settings: 'settings',
};

class Session {
	static isSignedIn = false;

	static accountInfo = {};

	static internalAccountInfo = {};

	static preferences = {};

	static featureflags = {};

	static brand = {};

	static settings = {};

	static calculatedSettings = {}; // This object will not change once set on page init

	static dynamicSettings = {}; // if you would like to subscribe to changes to dynamicSettings, please check for 'accountInfo' change type

	static subscribers = [];

	static sessionTimeoutTimer = null;

	static triggerTimeout = () => {};

	static isFormStateDirty = false;

	static init(settings = {}, sessionTimeoutHandler) {
		const { storeContext } = settings;

		// convert storeContext value to lowercase for matching against `STORE_CONTEXT_TYPE`
		if (isString(storeContext)) {
			settings.storeContext = storeContext.toLowerCase();
		}

		if (isFunction(sessionTimeoutHandler)) {
			this.triggerTimeout = sessionTimeoutHandler;
		}

		this.settings = { ...this.settings, ...settings };
	}

	static async authenticateWithToken() {
		const authenticator = Util.getQueryParamByName('Authenticator');
		const transferAuthenticator = Util.getQueryParamByName('TransferAuthenticator');
		const altAuth = transferAuthenticator ? Util.getQueryParamByName('altAuth') : null;

		if (authenticator || transferAuthenticator) {
			// remove auth string from the URL
			Util.removeQueryParamByName([
				'Authenticator',
				'TransferAuthenticator',
				'AltAuth',
				'isAuth0SignInMode',
			]);

			// call authenticateUser. Response variable not needed, since authentication state will be tested with session call
			await Communication.authenticateUser({
				SwsimAuth: authenticator,
				TransferAuth: transferAuthenticator,
				AltAuth: altAuth,
			});
		}
	}

	static async verifyAuthAndLoad(params, callback) {
		const isAuth0SignInMode = Util.getQueryParamByName('isAuth0SignInMode') === 'true' || false;

		// authenticate if auth or transfer token is present
		await this.authenticateWithToken();

		const response = await Communication.getSession(params);
		const data = isObject(response.data) ? response.data : {};

		if (Communication.isSuccessful(response)) {
			if (isObject(data.settings)) {
				this.settings = {
					...this.settings,
					...data.settings,
					isInWebStore: this.settings.storeContext === STORE_CONTEXT_TYPE.WEBSTORE,
					isInAtgStore: this.settings.storeContext === STORE_CONTEXT_TYPE.ATGSTORE,
					isIntegrator: this.settings.storeContext === STORE_CONTEXT_TYPE.INTEGRATOR,
				};

				// per WEBAPPS-20162, IsInWinClient should be `true` when storeContext is for winClient
				if (this.settings.storeContext === STORE_CONTEXT_TYPE.WINCLIENT) {
					this.settings.IsInWinClient = true;
				}
			}

			this.isSignedIn = data.SignedIn === true;
			this.accountInfo = isObject(data.accountInfo) ? Util.toCamelCase(data.accountInfo) : {};
			this.balance = isObject(data.balance) ? Util.toCamelCase(data.balance) : {};
			this.internalAccountInfo = isObject(data.internalAccountInfo)
				? data.internalAccountInfo
				: {};
			this.preferences = isObject(data.preferences) ? data.preferences : {};
			this.brand = isObject(data.brand) ? data.brand : {};
			this.calculatedSettings = {
				isSso: this.settings.Sso ? this.settings.Sso.length > 1 : false,
				ssoUrl: `${this.settings.SsoBaseUrl}/${this.settings.Sso}`,
			};

			if (data.SignedIn === true && isAuth0SignInMode === true) {
				ChatbotInitializer.resetChatMessengerUserPrefs();
			}
		}

		const configuredCarriers = this.accountInfo?.account?.configuredCarriers || [];
		if (!isEmpty(configuredCarriers)) {
			this.accountInfo.account.configuredCarriers = await this.customerCarrierDetails(
				configuredCarriers
			);
		}

		this.notifySubscribers(ChangeType.All);

		if (this.isSignedIn) {
			this.resetTimeout();

			// fires another call to get container label count
			this.loadContainerLabelCount(this.accountInfo.account);

			// fires another call to get container label count
			this.loadRefundAssistLabelCount(this.accountInfo.account);
		}

		if (isFunction(callback)) {
			callback();
		}
	}

	static resetTimeout = () => {
		// set default timeout to 2hours
		const logOffMs = 2 * 60 * 60 * 1000;

		// clear timer if it has already been created
		if (this.sessionTimeoutTimer) {
			clearTimeout(this.sessionTimeoutTimer);
		}

		// execute function once user times out
		this.sessionTimeoutTimer = setTimeout(this.triggerTimeout, logOffMs);
	};

	// Setters

	/**
	 * Update "accountInfo" data
	 * @param {object} accountInfo
	 */
	static setAccountInfo(accountInfo) {
		this.accountInfo = isObject(accountInfo) ? accountInfo : this.accountInfo;

		this.notifySubscribers(ChangeType.AccountInfo);
	}

	/**
	 * Update "preferences" data
	 * @param {object} preferences
	 */
	static setPreferences(preferences) {
		this.preferences = isObject(preferences) ? preferences : this.preferences;

		this.notifySubscribers(ChangeType.Preferences);
	}

	/**
	 * Update "balance" data
	 * @param {object} balance
	 */
	static setBalance(balance) {
		this.balance = isObject(balance) ? balance : this.balance;

		this.notifySubscribers(ChangeType.Balance);
	}

	static setContainerLabelCount(count) {
		this.dynamicSettings.containerLabelCount = count;
		this.notifySubscribers(ChangeType.ContainerLabelCount);
	}

	static setRefundAssistLabelCount(count) {
		this.dynamicSettings.refundAssistLabelCount = count;
		this.notifySubscribers(ChangeType.RefundAssistLabelCount);
	}

	static setFormStateDirty(isDirty) {
		this.isFormStateDirty = isDirty;
	}

	// Getters

	/**
	 * return all the properties including account, internalAccountInfo,
	 * preferences, balance, brand, settings and isSignedIn.
	 * @returns {object}
	 */
	static getData() {
		return {
			accountInfo: this.getAccountInfo(),
			internalAccountInfo: this.getInternalAccountInfo(),
			preferences: this.getPreferences(),
			brand: this.getBrand(),
			balance: this.getBalance(),
			settings: this.getSettings(),
			calculatedSettings: this.getCalculatedSettings(),
			dynamicSettings: this.getDynamicSettings(),
			role: this.getUserRole(),
			isSignedIn: this.isSignedIn,
		};
	}

	/**
	 * Get "account" data
	 * @returns {object}
	 */
	static getAccountInfo() {
		return isObject(this.accountInfo?.account) ? this.accountInfo.account : {};
	}

	/**
	 * Get "internalAccountInfo" data
	 * @returns {object}
	 */
	static getInternalAccountInfo() {
		return isObject(this.internalAccountInfo) ? this.internalAccountInfo : {};
	}

	/**
	 * Get "preferences" data
	 * @returns {object}
	 */
	static getPreferences() {
		return isObject(this.preferences) ? this.preferences : {};
	}

	/**
	 * Get "brand" data
	 * @returns {object}
	 */
	static getBrand() {
		return isObject(this.brand) ? this.brand : {};
	}

	/**
	 * Get "balance" data
	 * @returns {object}
	 */
	static getBalance() {
		return isObject(this.balance) ? this.balance : {};
	}

	/**
	 * Get "settings" data
	 * @returns {object}
	 */
	static getSettings() {
		return isObject(this.settings) ? this.settings : {};
	}

	/**
	 * Get "calculatedSettings" data
	 * @returns {object}
	 */
	static getCalculatedSettings() {
		return isObject(this.calculatedSettings) ? this.calculatedSettings : {};
	}

	static getDynamicSettings() {
		// Note: if you would like to subscribe to changes to dynamicSettings, please check for accountInfo change type
		return this.dynamicSettings;
	}

	/**
	 * Get "ratesetType" value
	 * @returns {string|null}
	 */
	static getRateSetType() {
		const { internalAccountDetails = {} } = this.getAccountInfo();

		return isString(internalAccountDetails.ratesetType)
			? internalAccountDetails.ratesetType
			: null;
	}

	static getContainerLabelCount() {
		if (this.dynamicSettings.containerLabelCount !== undefined) {
			return this.dynamicSettings.containerLabelCount;
		}

		return 0;
	}

	static getRefundAssistLabelCount() {
		if (this.dynamicSettings.refundAssistLabelCount !== undefined) {
			return this.dynamicSettings.refundAssistLabelCount;
		}

		return 0;
	}

	/**
	 * Calculate and return an UserRole type (See UserRole.js)
	 * @returns {UserRole}
	 */
	static getUserRole = () => {
		const { corporationId } = this.getAccountInfo();
		const { UserTier: userTier } = this.getInternalAccountInfo();

		switch (userTier) {
			// Corporate users
			case 0:
				return this.hasCapability(InternalCapability.ManageUsers)
					? UserRole.CorpAdmin
					: UserRole.CorpUser;

			// Region users
			case 1:
				return this.hasCapability(InternalCapability.ManageUsers)
					? UserRole.RegionAdmin
					: UserRole.CorpUser;

			// MultiUser users
			case 2:
				if (this.hasCapability(InternalCapability.ManageUsers)) {
					return corporationId === 0
						? UserRole.MultiAdmin // non-corp account when corp id is 0
						: UserRole.LocationAdmin; // corp account
				} else {
					return corporationId === 0
						? UserRole.MultiUser // non-corp account when corp id is 0
						: UserRole.CorpUser; // corp user account
				}

			// SingleUser users
			case 3:
			default:
				return UserRole.SingleUser;
		}
	};

	/**
	 * Get user's capabilities to determine whether he/she is allow to see carrier rates
	 * @returns {object}
	 */
	static getCarrierCapabilities = () => {
		const account = this.getAccountInfo();
		const configuredCarriers = isArray(account.configuredCarriers)
			? account.configuredCarriers.reduce(
					(_result, config) => ({
						..._result,
						[config.carrier]: true,
					}),
					{}
			  )
			: {};

		// WEBAPPS-30963, show UPS/DHL Express/Fedex rates if one of the following conditions is met:
		// (1) carrier is added as a connected carrier or
		// (2) "display carrier by default" capability is true
		const showUPSRates =
			configuredCarriers[Carrier.UPS] === true ||
			this.hasCapability(InternalCapability.DisplayUpsByDefault);

		const showDHLExpressRates =
			configuredCarriers[Carrier.DHLExpress] === true ||
			this.hasCapability(InternalCapability.DisplayDhlByDefault);

		const showFedexRates =
			configuredCarriers[Carrier.FedEx] === true ||
			this.hasCapability(InternalCapability.DisplayFedexByDefault);

		const showGlobalPost =
			this.hasCapability(Capability.Globalpost) ||
			this.hasCapability(Capability.GlobalPostPlus) ||
			this.hasCapability(Capability.GlobalPostDomestic);

		return {
			[Carrier.USPS]: true,
			[Carrier.UPS]: showUPSRates,
			[Carrier.DHLExpress]: showDHLExpressRates,
			[Carrier.FedEx]: showFedexRates,
			[Carrier.GlobalPost]: showGlobalPost,
		};
	};

	/**
	 * Get all user's capabilities and internalCapabilities
	 * @returns {array}
	 */
	static getCapabilties = () => {
		const { capabilities = [], internalAccountDetails = {} } = this.getAccountInfo();
		const internalCapabilities = isArray(internalAccountDetails.internalCapabilities)
			? internalAccountDetails.internalCapabilities
			: [];

		return [...capabilities, ...internalCapabilities];
	};

	/**
	 * Return true/false to indicate whether the user has the capability and internalCapabilities
	 * @param {Capability|InternalCapability} appCap
	 * @returns {boolean}
	 */
	static hasCapability = appCap => this.getCapabilties().includes(appCap);

	// Utilities
	static subscribe(callback) {
		// Note: if you would like to subscribe to changes to dynamicSettings, please check for accountInfo change type
		this.subscribers = [...this.subscribers, callback];

		// send subscriber index to component to be used for unsubscribing
		return this.subscribers.length - 1;
	}

	static unsubscribe(index) {
		this.subscribers[index] = null;
	}

	static notifySubscribers(changedType) {
		if (this.subscribers.length > 0) {
			this.subscribers.map((subscription, i) => {
				if (this.subscribers[i] !== null) {
					return subscription(changedType);
				}

				return null;
			});
		}
	}

	static async loadAccountInfo() {
		const response = await Communication.getAccountInfo();
		const info = Communication.isSuccessful(response) ? Util.toCamelCase(response.data) : {};

		if (isArray(info?.account?.configuredCarriers))
			info.account.configuredCarriers = await this.customerCarrierDetails(
				info.account.configuredCarriers
			);

		this.setAccountInfo(info);
	}

	static async loadBalance() {
		const response = await Communication.getBalance();
		const balance = Communication.isSuccessful(response) ? Util.toCamelCase(response.data) : {};

		this.setBalance(balance);
	}

	static async loadContainerLabelCount() {
		const response = await Communication.containerLabelCount();

		if (Communication.isSuccessful(response) && isObject(response.data)) {
			this.setContainerLabelCount(response.data.prints.length);
		}
	}

	static async loadRefundAssistLabelCount() {
		const response = await Communication.refundAssistLabelCount();

		if (Communication.isSuccessful(response) && isObject(response.data)) {
			this.setRefundAssistLabelCount(response.data.prints.length);
		}
	}

	static async signOut(callback) {
		if (this.isSignedIn) {
			await Communication.signOut();

			if (callback) {
				callback();
			}
		}
	}

	/**
	 * @param {array} preferences
	 * This function takes in an array of preference objects, and sets them to Session's preferences.
	 * It will also notify subscribers and make an API call to setPreferences.
	 */
	static async savePreferences(preferences) {
		const response = await Communication.setPreferences(preferences);

		if (Communication.isSuccessful(response)) {
			const newPreferences = preferences.reduce((acc, curr) => {
				const { Name, Value } = curr;

				return { ...acc, [Name]: Value };
			}, {});

			this.preferences = { ...this.preferences, ...newPreferences };
			this.notifySubscribers(ChangeType.Preferences);
		}

		return response;
	}

	static setFeatureFlags(flags) {
		this.featureflags = flags;
	}

	static getFeatureFlags(flag) {
		return this.featureflags[flag];
	}

	static customerCarrierDetails = async configuredCarriers => {
		const response = await Communication.getCarriers();

		if (Communication.isSuccessful(response)) {
			const carrierList = Util.toCamelCase(response.data);

			const updatedConfiguredCarriers = configuredCarriers.map(configuredCarrier => {
				if (configuredCarrier.userOwnedAccount) {
					const carrierObj = isArray(carrierList)
						? carrierList.find(
								c => c?.carrier?.toLowerCase() === configuredCarrier?.carrier
						  )
						: {};

					return { ...configuredCarrier, accountNumber: carrierObj?.accountNumber };
				}

				return configuredCarrier;
			});
			return updatedConfiguredCarriers;
		}
	};

	static isFreemiumUser = () => {
		const { internalAccountDetails = {} } = this.getAccountInfo();
		const { customerGroup } = internalAccountDetails;

		return (
			(customerGroup === '101' || customerGroup === '102') &&
			!this.hasCapability(Capability.Usps)
		);
	};

	static isAccountVerificationRequired = () => {
		const { accountStatus } = this.getAccountInfo();

		return accountStatus === AccountStatus.AccountVerificationRequired;
	};

	/**
	 * Checks carrier app caps and turns on the  flag 'hasAccessToMultipleCarriers'
	 * if user has access to atleast one other carrier besides Postal.
	 */
	static hasAccessToMultipleCarriers = () => {
		const account = this.getAccountInfo();
		const configuredCarriers = isArray(account.configuredCarriers)
			? account.configuredCarriers.reduce(
					(_result, config) => ({
						..._result,
						[config.carrier]: true,
					}),
					{}
			  )
			: {};

		if (
			this.hasCapability(InternalCapability.DisplayUpsByDefault) ||
			configuredCarriers.length > 0 ||
			this.isEligibleForGP() ||
			this.hasCapability(InternalCapability.DisplayFedexByDefault) ||
			this.hasCapability(InternalCapability.DisplayDhlByDefault)
		) {
			// sets the flag to indicate user has access to multiple carriers
			return true;
		}

		return false;
	};

	static isEligibleForGP = () =>
		this.hasCapability(Capability.Globalpost) ||
		this.hasCapability(Capability.GlobalPostPlus) ||
		this.hasCapability(Capability.GlobalPostDomestic);

	static showBillingStatusModal = () => {
		const {
			subscriptionStatus: {
				adminHold = false,
				resubmit = false,
				serviceFeesNotPaid = false,
				paymentHold = false,
			} = {},
		} = this.getAccountInfo();

		return adminHold || resubmit || serviceFeesNotPaid || paymentHold;
	};

	static canAccessPilotSuppliesStore = () => {
		return this.hasCapability(Capability.AccessPilotSuppliesStore);
	}
}

export default Session;
